import * as React from "react";
import { defaultQuestionFlowKnockoutAction } from "kw/components/QuestionFlow/QuestionFlowComponent";
import { FlowLayout } from "kw/components/FlowLayout";
import { isEmpty } from "kanopi-components/utils/primitives";
import { StatelessQuote } from "../components/StatelessQuote";
import { toaster } from "kanopi-components/ToastContainer";
import { useCovers } from "../hooks/useCovers";
import { useGlobal } from "kanopi-components/useGlobal";
import { useNavigate } from "@reach/router";
import { usePriceRiskProfile } from "../hooks/usePriceRiskProfile";
import { useStartQuestionFlow } from "kw/hooks/useStartQuestionFlow";
import { SidePadder } from "kanopi-components/SpaceWrapper/SidePadder";
import {
  enquiryFields,
  questionSetFields,
  questionFields,
  riskProfileFields,
} from "kw/api/fragments";
import { QuestionFlowTypeKey } from "kw/components/QuestionFlow/QuestionFlowComponent";

function Quote(props) {
  const navigate = useNavigate();
  const [data = {}] = useGlobal("quoting");
  const [
    ,
    riskProfile,
    { priceRiskProfile, pricingFailed, priceLoading, initialPriceLoading },
  ] = usePriceRiskProfile();

  const [coverSections, { loading: coversLoading }] = useCovers();
  const { enquiry, questionSet, maxSets, currentSet } = useStartQuestionFlow({
    mutationQuery: getStartQuoteQuery(data?.riskProfile?.id),
    globalDataKey: QuestionFlowTypeKey.quote,
    questionFlowResponseKey: "startQuote",
    onQuestionFlowKnockout: defaultQuestionFlowKnockoutAction,
  });

  React.useEffect(() => {
    const allQuestionsAnswered = currentSet > 0 && currentSet === maxSets;
    const questionAvailableToAnswer = riskProfile?.id && questionSet?.key;
    // We only want to price/redirect if this is not a modal cover flow
    if (!data?.isCoverFlow) {
      if (allQuestionsAnswered) {
        priceRiskProfile();
      } else if (questionAvailableToAnswer) {
        navigate(`/quoting/${riskProfile.id}/${questionSet.key}`);
      } else if (enquiry?.emptyQuestionFlow && isEmpty(enquiry?.riskProfile?.data)) {
        // Someone accessing the site directly without selecting a product.
        navigate(`/quote/referral/${enquiry?.riskProfile?.id}`);
      }
    }
  }, [currentSet, maxSets]);

  React.useEffect(() => {
    const { messages = [] } = data;
    messages.forEach(({ kind, content }) => {
      toaster({ kind, content });
    });
  }, [data?.messages]);

  const [, setTrackingData] = useGlobal("tracking");
  React.useEffect(() => {
    if (!priceLoading || coversLoading) {
      setTrackingData({
        riskProfile: riskProfile,
        enabledCoverSections: riskProfile?.enabledCovers,
      });
    }
  }, [priceLoading, coversLoading]);

  return (
    <SidePadder padding="flow">
      <FlowLayout
        steps={[
          { label: "Your details", complete: true },
          { label: "Your quote", complete: true, active: true },
          { label: "Review and buy" },
        ]}
        history={[...(data?.history || []), { key: "/quote/ready" }]}
      >
        <StatelessQuote
          {...props}
          riskProfile={riskProfile}
          quotes={riskProfile?.quotes}
          coverSections={coverSections}
          loading={coversLoading || initialPriceLoading}
          priceLoading={priceLoading}
          pricingFailed={pricingFailed}
          priceRiskProfile={priceRiskProfile}
        />
      </FlowLayout>
    </SidePadder>
  );
}

function getStartQuoteQuery(riskProfileId?: string) {
  return {
    startQuote: [
      {
        input: {
          questionSetKey: undefined,
          create: false,
          riskProfileId: riskProfileId,
        },
      },
      {
        currentEnquiry: {
          ...enquiryFields,
          questionSet: {
            ...questionSetFields,
            questions: {
              ...questionFields,
            },
          },
        },
        riskProfile: {
          ...riskProfileFields,
        },
        history: {
          questionSet: {
            id: true,
            key: true,
            label: true,
          },
        },
        maxSets: true,
        currentSet: true,
      },
    ],
  };
}

export { Quote };
